import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { LineReveal } from "./Reveal";
import { useSound } from "../../hooks/useSound";
import { LANGUAGES } from "../../lib/content";

const scrollTo = (hash) => {
  if (window.__lenis) window.__lenis.scrollTo(hash, { offset: -60, duration: 1.4 });
  else document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
};

export const Hero = () => {
  const ref = useRef(null);
  const { pluck } = useSound();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);

  return (
    <section id="hero" ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col justify-end md:flex-row md:items-end overflow-hidden">
      {/* spotlight */}
      <div
        aria-hidden
        className="spotlight absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse 60% 55% at 72% 38%, rgba(239,55,44,0.10), transparent 65%)" }}
      />

      {/* photo — clipped frame, parallax */}
      <motion.div
        style={{ y: imgY }}
        className="relative w-full h-[44vh] sm:h-[52vh] md:absolute md:right-0 md:top-0 md:h-[92vh] md:w-[44%]"
      >
        <motion.div
          initial={{ clipPath: "inset(100% 0 0 0)" }}
          animate={{ clipPath: "inset(0% 0 0 0)" }}
          transition={{ duration: 1.4, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="w-full h-full"
        >
          <img
            src="/photos/hero-stage.webp"
            alt="Sanjith performing live on stage"
            className="w-full h-full object-cover"
          />
        </motion.div>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 1 }}
          className="absolute bottom-8 right-6 font-mono text-[10px] tracking-[0.3em] uppercase text-[#4a3e30] rotate-90 origin-bottom-right hidden md:block"
        >
          Live Performance — India
        </motion.p>
      </motion.div>

      {/* headline */}
      <motion.div style={{ y: textY }} className="relative z-10 w-full max-w-[1600px] mx-auto px-6 md:px-12 pb-16 md:pb-28 pt-8 md:pt-40">
        <LineReveal delay={0.2}>
          <p data-testid="hero-kicker" className="font-mono text-xs tracking-[0.4em] uppercase text-[#950606]">
            Guitarist · Teacher · Indie Musician · Est. 2020
          </p>
        </LineReveal>

        <h1 className="mt-8 font-display font-black text-[#1c1610] text-5xl sm:text-7xl lg:text-[7.5rem] leading-[0.95] tracking-tight">
          <LineReveal delay={0.45}>Play it like</LineReveal>
          <LineReveal delay={0.65}>
            <span>
              you <em className="text-[#DB0909] not-italic font-display italic">say</em> it.
            </span>
          </LineReveal>
        </h1>

        <LineReveal delay={0.9} className="mt-8 max-w-md">
          <p className="text-[#4a3e30] text-base md:text-lg font-light leading-relaxed">
            Stage-tested, soul-first guitar training with Sanjith. Real musicianship from your first chord to your first stage.
          </p>
        </LineReveal>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12 flex flex-wrap items-center gap-5"
        >
          <button
            data-testid="hero-book-btn"
            onMouseEnter={() => pluck(196)}
            onClick={() => scrollTo("#contact")}
            className="vibe font-mono text-xs tracking-[0.25em] uppercase bg-[#1c1610] text-[#f7f6f2] px-8 py-4 hover:bg-[#DB0909] active:bg-[#950606] transition-colors duration-300"
          >
            Book a Spot →
          </button>
          <button
            data-testid="hero-courses-btn"
            onMouseEnter={() => pluck(146.8)}
            onClick={() => scrollTo("#courses")}
            className="vibe font-mono text-xs tracking-[0.25em] uppercase border border-[rgba(28,22,16,0.35)] text-[#1c1610] px-8 py-4 hover:border-[#950606] hover:text-[#950606] transition-colors duration-300"
          >
            Explore Courses
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="mt-10 md:mt-16 flex flex-wrap items-center gap-2 sm:gap-3"
          data-testid="hero-language-strip"
        >
          <span className="font-mono text-[10px] sm:text-[11px] tracking-[0.3em] uppercase text-[#950606] mr-1">Taught in</span>
          {LANGUAGES.map((l) => (
            <span
              key={l}
              className="inline-flex items-center font-mono text-[10px] sm:text-[11px] tracking-[0.25em] uppercase text-[#1c1610] border border-[rgba(28,22,16,0.35)] px-3 py-1.5 bg-[#f7f6f2]/60"
            >
              {l}
            </span>
          ))}
        </motion.div>
      </motion.div>

      {/* scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2"
      >
        <span className="font-mono text-[10px] tracking-[0.35em] uppercase text-[#4a3e30]">Scroll</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }} className="w-px h-8 bg-[#950606]" />
      </motion.div>
    </section>
  );
};
